import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import {
  applyAnnouncementVars,
  markAnnouncementSeen,
  markAnnouncementDismissed,
} from "../services/announcementService";

// Dashboard popup dialog for an admin-authored in-app announcement.
// Styled to match the Jurident dark theme. Closing is only via the X — it
// also records the dismissal.
export default function AnnouncementPopup({ announcement, uid, vars, onClose }) {
  const navigate = useNavigate();

  // Record the impression as soon as the popup is shown.
  useEffect(() => {
    if (announcement && uid) markAnnouncementSeen(announcement, uid);
  }, [announcement, uid]);

  if (!announcement) return null;

  const a = announcement;
  const title = applyAnnouncementVars(a.title, vars);
  const body = applyAnnouncementVars(a.body, vars);

  // The X closes the popup and records the dismissal. The popup closes
  // immediately — the Firestore write runs in the background so a slow
  // network can never block the close.
  const dismiss = () => {
    markAnnouncementDismissed(a, uid);
    onClose();
  };

  const handleCta = () => {
    markAnnouncementDismissed(a, uid);
    onClose();
    if (!a.ctaUrl) return;
    if (/^https?:\/\//i.test(a.ctaUrl)) {
      window.open(a.ctaUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(a.ctaUrl);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#050125] border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="p-5 relative">
          <button
            onClick={dismiss}
            aria-label="Close"
            className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={16} />
          </button>

          {/* Title — at the top */}
          <h2 className="text-base font-bold text-[#C99F4A] text-center px-6 leading-snug">{title}</h2>

          {/* Divider below the title */}
          <div className="mt-3 border-t border-white/10" />

          {/* Small logo-style image — below the title */}
          {a.imageUrl ? (
            <div className="mt-3 flex justify-center">
              <img
                src={a.imageUrl}
                alt=""
                className="w-14 h-14 rounded-xl object-cover border border-white/10"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          ) : null}

          <p className="text-sm text-gray-300 mt-3 text-center whitespace-pre-wrap">{body}</p>

          {a.ctaLabel ? (
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleCta}
                className="bg-[#C99F4A] hover:bg-[#b78f3e] text-[#050125] px-4 py-2 rounded-full text-sm font-semibold transition-colors"
              >
                {a.ctaLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
